import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Search, DoorClosed, ChevronRight } from "lucide-react";
import { buildingsApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import PageHeader from "@/components/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

export default function PortalBuildings() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAsync(() => buildingsApi.getAll(), []);
  const [q, setQ] = useState("");
  const list = Array.isArray(data) ? data : [];
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? list.filter((b) => `${b.name} ${b.address || ""} ${b.postcode || ""}`.toLowerCase().includes(s)) : list;
  }, [list, q]);

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader title="Your buildings" subtitle="Fire-door compliance across your properties" testId="portal-buildings-header" />
      {loading && <LoadingState label="Loading buildings…" />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}
      {!loading && !error && list.length === 0 && <EmptyState title="No buildings yet" message="Buildings your provider manages for you will appear here." />}
      {!loading && !error && list.length > 0 && (
        <>
          <div className="relative mb-4 max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search buildings…" className="pl-9" data-testid="portal-buildings-search" />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {filtered.map((b) => (
              <button key={b.id} onClick={() => navigate(`/portal/buildings/${b.id}`)} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left transition-shadow hover:shadow-md" data-testid={`portal-building-${b.id}`}>
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"><Building2 className="h-6 w-6" /></div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-slate-800">{b.name}</p>
                  <p className="truncate text-sm text-slate-500">{[b.address, b.postcode].filter(Boolean).join(", ")}</p>
                  {b.door_count != null ? <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-400"><DoorClosed className="h-3.5 w-3.5" /> {b.door_count} doors</p> : null}
                </div>
                <ChevronRight className="h-5 w-5 shrink-0 text-slate-400" />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
